/**
 * AttachmentPanel — File attachment list with upload / download / delete.
 *
 * Props:
 *   entityName — e.g. "GlJournal"
 *   entityId   — UUID of the entity
 *   canAdd     — whether the current user has DOC_ATTACHMENT_ADD permission
 *   canDelete  — whether the current user has DOC_ATTACHMENT_DELETE permission
 */

import { useEffect, useState, useRef } from 'react';
import { Paperclip, Upload, Trash2, Download, AlertCircle, RefreshCcw } from 'lucide-react';
import { attachmentApi } from '@/api/attachment.api';
import type { AttachmentDto } from '@/types/attachment.types';
import { formatFileSize, getFileIcon } from '@/types/attachment.types';
import { cn } from '@/utils/cn';
import { Spinner } from '@/components/ui/Spinner';

interface AttachmentPanelProps {
  entityName: string;
  entityId:   string;
  canAdd?:    boolean;
  canDelete?: boolean;
  className?: string;
}

export const AttachmentPanel = ({
  entityName,
  entityId,
  canAdd    = false,
  canDelete = false,
  className,
}: AttachmentPanelProps) => {
  const [attachments, setAttachments] = useState<AttachmentDto[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [uploading,   setUploading]   = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    if (!entityId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await attachmentApi.getByEntity(entityName, entityId);
      setAttachments(res.data.data ?? []);
    } catch {
      setError('Failed to load attachments.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [entityName, entityId]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await attachmentApi.upload(entityName, entityId, file);
      await load();
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      // Reset so the same file can be uploaded again if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete attachment "${name}"?`)) return;
    try {
      await attachmentApi.delete(id);
      setAttachments(prev => prev.filter(a => a.id !== id));
    } catch {
      setError('Delete failed.');
    }
  };

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Paperclip size={16} className="text-slate-400" />
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Attachments
            {attachments.length > 0 && (
              <span className="ml-1.5 text-xs font-normal text-slate-400">({attachments.length})</span>
            )}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="p-1.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100
                       dark:hover:bg-slate-700 transition-colors"
            title="Refresh"
          >
            <RefreshCcw size={13} />
          </button>

          {canAdd && (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                           bg-blue-600 hover:bg-blue-700 text-white rounded-lg
                           disabled:opacity-50 transition-colors"
              >
                {uploading ? <Spinner size="sm" /> : <Upload size={13} />}
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                className="hidden"
                accept="*/*"
              />
            </>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20
                        border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
          <AlertCircle size={13} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner size="sm" />
        </div>
      ) : attachments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-slate-400">
          <Paperclip size={32} className="mb-2 opacity-30" />
          <p className="text-sm">No attachments yet.</p>
          {canAdd && (
            <p className="text-xs mt-1 text-slate-400">
              Click Upload to attach files to this document.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {attachments.map(a => (
            <div
              key={a.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800
                         border border-slate-200 dark:border-slate-700 group"
            >
              {/* Icon */}
              <span className="text-xl leading-none flex-shrink-0" role="img" aria-label="file">
                {getFileIcon(a.contentType)}
              </span>

              {/* File info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                  {a.originalFileName}
                </p>
                <p className="text-xs text-slate-400">
                  {formatFileSize(a.fileSize)}
                  {a.category && ` · ${a.category}`}
                  {` · ${new Date(a.uploadedDate).toLocaleDateString()}`}
                  {a.uploadedByName && ` · ${a.uploadedByName}`}
                </p>
                {a.remarks && (
                  <p className="text-xs text-slate-500 dark:text-gray-400 italic mt-0.5">{a.remarks}</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <a
                  href={attachmentApi.getDownloadUrl(a.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={a.originalFileName}
                  className="p-1.5 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50
                             dark:hover:bg-blue-900/20 transition-colors"
                  title="Download"
                >
                  <Download size={13} />
                </a>

                {canDelete && (
                  <button
                    onClick={() => handleDelete(a.id, a.originalFileName)}
                    className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50
                               dark:hover:bg-red-900/20 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
